import { supportedLanguages, type SupportedLanguage } from './languages'

/**
 * Define localized routes here.
 * Each key maps to a route name, with translations for each language.
 *
 * Example:
 *   about: { it: 'chi-siamo', en: 'about-us' }
 *   → /it/chi-siamo, /en/about-us
 */
export const localizedRoutes = {
  home: { it: '', en: '' },
  services: { it: 'servizi', en: 'services' },
  landingWeb: { it: 'servizi/siti-web', en: 'services/websites' },
  landingAutomation: { it: 'servizi/automazioni', en: 'services/automation' },
  automation: { it: 'potenziale-automazione', en: 'automation-potential' },
  contacts: { it: 'contatti', en: 'contacts' },
  privacy: { it: 'privacy', en: 'privacy' },
  // Vecchio indirizzo della cookie policy (stessa pagina della privacy): redirect permanente, vedi routes/cookie-policy.tsx.
  cookiePolicy: { it: 'cookie-policy', en: 'cookie-policy' },
  login: { it: 'login', en: 'login' },
  admin: { it: 'admin', en: 'admin' },
  // Consenso OAuth dei client MCP (es. connettori claude.ai): consentPage in apps/backend/src/auth.ts.
  oauthConsent: { it: 'autorizza', en: 'authorize' },
  // Annullamento di una prenotazione dal link nell'email (@sepma/calendar): uguale a CANCEL_PATHS in apps/backend/src/lib/calendar.ts.
  bookingCancel: { it: 'prenotazione/annulla/:token', en: 'booking/cancel/:token' },
} as const

export type RouteKey = keyof typeof localizedRoutes

/** Percorso di uno slug di rotta in una lingua; i parametri (`:slug`) vengono riempiti e codificati. */
export function fillPath(pattern: string, lang: SupportedLanguage, params: Record<string, string> = {}): string {
  const path = pattern.replace(/:(\w+)/g, (_, name: string) => encodeURIComponent(params[name] ?? ''))
  return path ? `/${lang}/${path}` : `/${lang}`
}

/** Percorso localizzato di una rotta (con i parametri delle pagine dinamiche, es. `{ slug }`). */
export function getLocalizedPath(routeKey: RouteKey, lang: SupportedLanguage, params: Record<string, string> = {}): string {
  return fillPath(localizedRoutes[routeKey][lang], lang, params)
}

/**
 * Find route key from a path segment
 */
export function findRouteKeyFromPath(
  pathSegment: string,
  lang: SupportedLanguage
): RouteKey | null {
  for (const [key, routes] of Object.entries(localizedRoutes)) {
    if (routes[lang] === pathSegment) {
      return key as RouteKey
    }
  }
  return null
}

/**
 * Pagine pubbliche: prerenderizzate, in sitemap, con meta SEO e JSON-LD.
 * Ogni voce ha il suo route module in src/routes/<chiave>.tsx e le chiavi seo.<chiave>.* e nav.<chiave> nei locales.
 */
export const publicPages = [
  'home',
  'services',
  'landingWeb',
  'landingAutomation',
  'automation',
  'contacts',
  'privacy',
] as const satisfies readonly RouteKey[]
export type PublicPage = (typeof publicPages)[number]

/**
 * Pagine dinamiche: dati dall'API, generate dal server a ogni richiesta (loader), es. `events: { it: 'eventi/:slug' }`.
 * Route module in src/routes/dynamic/ (vedi src/routes.ts e "Aggiungere una pagina dinamica" nel README).
 */
export const dynamicPages = [] as const satisfies readonly RouteKey[]
export type DynamicPage = (typeof dynamicPages)[number]

/** Landing delle campagne: niente header, footer e chatbot del sito (routes/landing-layout.tsx). */
export const landingPages = ['landingWeb', 'landingAutomation'] as const satisfies readonly PublicPage[]

/** Voci di menu di header e footer, nell'ordine di visualizzazione (pagine statiche; anche elenchi dinamici). */
export type NavPage = PublicPage
export const navPages: readonly NavPage[] = ['home', 'services', 'contacts']

/** Pagine private: generate dal server senza dati (sessione e dati arrivano dal browser), mai prerenderizzate né indicizzate. */
export const privatePages = ['login', 'admin', 'oauthConsent', 'bookingCancel'] as const satisfies readonly RouteKey[]

/** Tutti i percorsi pubblici, in ogni lingua: lista di prerender, sitemap e controllo SEO. */
export function publicPaths(): string[] {
  return publicPages.flatMap((page) => supportedLanguages.map((lang) => getLocalizedPath(page, lang)))
}

/** Login e area riservata (e tutto ciò che sta sotto), in qualsiasi lingua. */
export function isPrivatePath(pathname: string): boolean {
  const [, lang, slug] = pathname.split('/')
  return supportedLanguages.some(
    (l) =>
      l === lang &&
      privatePages.some((page) => {
        const route: string = localizedRoutes[page][l]
        // Rotta con più segmenti (`prenotazione/annulla/:token`): deve corrispondere per intero.
        return route.includes('/') ? matchesLocalizedRoute(pathname, [localizedRoutes[page]]) : route === slug
      }),
  )
}

/** `:param` corrisponde a un segmento non vuoto; gli altri segmenti devono coincidere. */
function matchesPattern(pattern: string, segments: string[]): boolean {
  const parts = pattern.split('/').filter(Boolean)
  return parts.length === segments.length && parts.every((part, i) => (part.startsWith(':') ? segments[i] !== '' : part === segments[i]))
}

/** Il percorso (anche con slash finale) corrisponde a una delle rotte, nella lingua del percorso. */
export function matchesLocalizedRoute(pathname: string, routes: readonly Record<SupportedLanguage, string>[]): boolean {
  const [, lang, ...segments] = pathname.replace(/\/+$/, '').split('/')
  return supportedLanguages.some((l) => l === lang && routes.some((route) => matchesPattern(route[l], segments)))
}

/** Pagine dinamiche, in qualsiasi lingua: generate dal server, mai prerenderizzate. */
export function isDynamicPath(pathname: string): boolean {
  return matchesLocalizedRoute(pathname, dynamicPages.map((page) => localizedRoutes[page]))
}

/**
 * Stessa pagina in un'altra lingua: `alternates` (dati della rotta, es. slug tradotti di una pagina dinamica) ha la
 * precedenza; poi le rotte senza parametri tradotte con localizedRoutes; altrimenti la home della lingua
 * (una pagina dinamica senza equivalente non deve portare a una 404).
 */
export function targetPathForLanguage(
  pathname: string,
  target: SupportedLanguage,
  alternates?: Partial<Record<SupportedLanguage, string>>,
): string {
  if (alternates?.[target]) return alternates[target]
  const [, lang, ...segments] = pathname.replace(/\/+$/, '').split('/')
  const current = segments.join('/')
  const source = supportedLanguages.find((l) => l === lang)
  if (source) {
    for (const routes of Object.values(localizedRoutes)) {
      if (!routes[source].includes(':') && routes[source] === current) return routes[target] ? `/${target}/${routes[target]}` : `/${target}`
    }
    // Le sottopagine dell'area riservata (admin/users/…) non sono tradotte: si cambia solo il prefisso.
    const admin = localizedRoutes.admin
    if (current.startsWith(`${admin[source]}/`)) return `/${target}/${admin[target]}${current.slice(admin[source].length)}`
  }
  return `/${target}`
}
